/* ─────────────── SidePanel.jsx (portal version) ─────────────── */
import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import moment from "moment-timezone";
import styles from "./SidePanel.module.scss";
import Button from "./Button";

import playIcon from "../assets/playNowIcon.svg";
import undo from "../assets/undoButton.svg";
import addEventIcon from "../assets/addEvent.svg";
import removeEventIcon from "../assets/removeEvent.svg";
import warningEvent from "../assets/warningEvent.svg";
import { EventStatusDictionary } from "../helpers/eventHelper";

const OVERLAP_COPY = {
  SAME: "There is another event scheduled for this time. Unsubscribe from the initial event to play.",
  AFTER: "This event overlaps with an existing event and will play first in your schedule.",
  BEFORE: "This event overlaps with an existing event and will play upon completion of the existing event.",
};

const FUTURE_OVERLAP_CONFLICT_COPY = {
  SAME: "There is another event scheduled for this time. If you add event, this event will take priority.",
  AFTER: "This event overlaps with an existing event. If you add event, this event will play upon completion of the existing event.",
  BEFORE: "This event overlaps with an existing event. If you add event, this event will play first in your schedule."
};

/**
 * Renders the side panel in a portal (outside every transform context)
 * to avoid the scroll‑jump that happened when it lived inside the sheet.
 */
export default function SidePanel({
  isOpen,
  event,
  onClose,
  onPlay,
  ctaRef,
  backBtnRef,
  isNowPlaying,
  isLoading,
  tz = moment.tz.guess(),
}) {
  /* ------------------------------------------------------------
   * Guard clauses
   * ---------------------------------------------------------- */
  if (!isOpen || !event) return null; // nothing to show
  /* ------------------------------------------------------------
   * 1. Derived values
   * ---------------------------------------------------------- */
  const isUpcoming = ["SCHEDULED", "COMING"].includes(event.status);
  const isSubscribed = event.isDeviceSubscribed === true;

  let badgeText = isNowPlaying ? "NOW PLAYING" : "LIVE";

  if (isUpcoming) {
    const scheduleStartWithTz = moment.tz(event.schedule_start, tz);
    const isToday = scheduleStartWithTz.isSame(moment.tz(tz), 'day');
  
    const formatString = isToday ? "h:mm A" : "ddd MM/DD";
    const formattedTime = scheduleStartWithTz.format(formatString);
    
    badgeText = `UPCOMING ${formattedTime.toUpperCase()}`;
  }

  const badgeClass = `${styles.liveBadge} ${
    isUpcoming ? styles.upcoming : ""
  } ${isNowPlaying ? styles.nowPlaying : ""}`;

  let ctaText;
  let ctaIcon;
  let onCtaClick;

  if (event.status === EventStatusDictionary.LIVE && isNowPlaying) {
    ctaText = "Remove Event";
    ctaIcon = removeEventIcon;
    onCtaClick = () => onPlay(event);
  } else if (event.status === EventStatusDictionary.LIVE) {
    ctaText = "Play Now";
    ctaIcon = playIcon;
    onCtaClick = () => onPlay(event);
  } else if (isSubscribed) {
    ctaText = "Remove Event";
    ctaIcon = removeEventIcon;
    onCtaClick = () => onPlay(event);
  } else {
    ctaText = "Add Event";
    ctaIcon = addEventIcon;
    onCtaClick = () => onPlay(event);
  }

  // ── Overlap helpers ─────────────────────────────────────────
  const overlap = event?.orgOverlaps ?? {};

  const isOverlapped = overlap.isOverlapped;
  const willOverlap = !isOverlapped && overlap.overlapType && !isSubscribed;

  const showOverlapBanner = event.status !== EventStatusDictionary.LIVE && (isOverlapped || willOverlap);

  const bannerText =
    (willOverlap ? FUTURE_OVERLAP_CONFLICT_COPY : OVERLAP_COPY)[overlap.overlapType];

  const matchedLogos = event.teams
    .filter((team) =>
      team.alias.some((alias) =>
        event.title?.toLowerCase().includes(alias.toLowerCase())
      )
    )
    .map((team) => (
      <img
        key={team.id}
        src={team.logo}
        alt={team.display_name}
        width="130"
        height="130"
        className={styles.teamLogo}
      />
    ));

  /* ------------------------------------------------------------
   * 2. Focus management
   * ---------------------------------------------------------- */
  useEffect(() => {
    ctaRef.current?.focus({ preventScroll: true });
  }, []); // On open, focus the primary CTA

  /* ------------------------------------------------------------
   * 3. Mark‑up
   * ---------------------------------------------------------- */
  const panelMarkup = (
    <aside className={`${styles.panel} ${styles.open}`} aria-hidden={false}>
      {/* Back / Undo */}
      <button
        ref={backBtnRef}
        className={styles.backBtn}
        onClick={onClose}
        tabIndex={-1}
        aria-label="Close"
      >
        <img src={undo} alt="undo" />
      </button>

      {/* Badge */}
      <div className={styles.header}>
        <span className={badgeClass}>{badgeText}</span>
      </div>

      {/* Team logos */}
      <div className={styles.teamLogosWrapper}>{matchedLogos}</div>

      <h1 className={styles.title}>{event.title}</h1>

      <p className={styles.meta}>
        Pregame:&nbsp;
        {moment.tz(event.schedule_start, tz).format('h:mm A')}
        {" | "}Event start:&nbsp;
        {moment
            .tz(event.information?.main_start_time ?? event.schedule_start, tz)
            .format('h:mm A')}
        {event.hasPostgame && <span className={styles.meta}> {" | "} Includes Postgame</span>}

      </p>
      <p className={styles.desc}>{event.description}</p>
      <p className={styles.provider}>{event.label}</p>

      {showOverlapBanner && (
        <div className={styles.conflictBanner} role="alert">
          <img
            className={styles.warningIcon}
            src={warningEvent}
            alt="Scheduling conflict"
          />
          <p className={styles.bannerCopy}>{bannerText}</p>
        </div>
      )}
      <Button
        ref={ctaRef}
        onClick={onCtaClick}
        text={ctaText}
        bgColor={
          (ctaText.includes("Remove") || isLoading) ? "rgba(255, 255, 255, 0.32)" : "#fff"
        }
        loading={isLoading}
        textColor={ctaText.includes("Remove") || isLoading ? "#fff" : "#000"}
        className={`${styles.buttonCta} ${!showOverlapBanner && styles.marginTopCta} `}
        icon={<img src={ctaIcon} alt="" />}
      />
    </aside>
  );

  /* ------------------------------------------------------------
   * 4. Portal target
   * ---------------------------------------------------------- */
  const portalTarget = document.getElementById("sidepanel-root");

  return createPortal(panelMarkup, portalTarget);
}